// src/components/admin/NotifyDealersModal.jsx
import React, { useState } from 'react';

/* Same helper as DealerManagement — extracts car label from any inspection shape */
const getCarLabel = (ins) => {
  if (ins.car_details?.brand || ins.car_details?.model) {
    const { brand = '', model = '', year = '' } = ins.car_details;
    return `${brand} ${model} ${year}`.trim();
  }
  if (ins.brand || ins.model) return `${ins.brand || ''} ${ins.model || ''} ${ins.year || ''}`.trim();
  if (ins.car?.brand || ins.car?.model) return `${ins.car.brand || ''} ${ins.car.model || ''} ${ins.car.year || ''}`.trim();
  if (ins.sell_request) {
    const sr = ins.sell_request;
    return `${sr.car_brand || sr.brand || ''} ${sr.car_model || sr.model || ''} ${sr.year || ''}`.trim();
  }
  return `Inspection #${ins.id}`;
};

const NotifyDealersModal = ({ inspection, onClose, onSuccess, adminApi }) => {
  const [form, setForm] = useState({
    price_range_min: '',
    price_range_max: '',
    expected_price: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async () => {
    if (!form.price_range_min || !form.price_range_max || !form.expected_price) {
      setError('All price fields are required.');
      return;
    }
    if (Number(form.price_range_min) > Number(form.price_range_max)) {
      setError('Min price cannot exceed max price.');
      return;
    }
    if (Number(form.expected_price) <= 0) {
      setError('Expected price must be greater than 0.');
      return;
    }
    try {
      setLoading(true);
      const response = await adminApi.post(
        `/admin/car_inspections/${inspection.id}/notify_dealers`,
        {
          price_range_min: Number(form.price_range_min),
          price_range_max: Number(form.price_range_max),
          expected_price:  Number(form.expected_price),
        }
      );
      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to notify dealers.');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (val) =>
    val ? `₹${Number(val).toLocaleString('en-IN')}` : '—';

  const carLabel = getCarLabel(inspection);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content notify-dealers-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        {/* Header */}
        <div className="modal-header">
          <h2 style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{
              background:'linear-gradient(135deg,#FFD60A,#FFC107)',
              borderRadius:8, padding:'6px 10px', fontSize:18,
            }}>📢</span>
            Notify Dealers
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Inspection Summary */}
          <div style={{
            background:'linear-gradient(135deg,#003566,#001d3d)',
            borderRadius:12, padding:'16px 20px', marginBottom:24,
            border:'2px solid #FFD60A',
          }}>
            <p style={{ color:'rgba(255,214,10,0.9)', fontSize:11, fontWeight:700,
              textTransform:'uppercase', letterSpacing:1, margin:'0 0 6px' }}>
              Inspection Details
            </p>
            <p style={{ color:'#fff', fontSize:16, fontWeight:700, margin:'0 0 4px' }}>
              {carLabel || `Inspection #${inspection.id}`}
            </p>
            <p style={{ color:'rgba(255,255,255,0.7)', fontSize:13, margin:0 }}>
              Inspection #{inspection.id}
              {inspection.status && ` • Status: ${inspection.status}`}
              {inspection.booking_reference && ` • Ref: ${inspection.booking_reference}`}
            </p>
          </div>

          {/* Price Form */}
          <div className="pricing-form">
            <div className="form-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
              <div className="form-group">
                <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#003566', marginBottom:6 }}>
                  Min Price (₹) *
                </label>
                <input
                  className="form-input price-input"
                  type="number"
                  name="price_range_min"
                  placeholder="e.g. 450000"
                  value={form.price_range_min}
                  onChange={handleChange}
                  min="1"
                />
                {form.price_range_min && (
                  <small style={{ display:'block', color:'#666', marginTop:4 }}>{fmt(form.price_range_min)}</small>
                )}
              </div>
              <div className="form-group">
                <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#003566', marginBottom:6 }}>
                  Max Price (₹) *
                </label>
                <input
                  className="form-input price-input"
                  type="number"
                  name="price_range_max"
                  placeholder="e.g. 550000"
                  value={form.price_range_max}
                  onChange={handleChange}
                  min="1"
                />
                {form.price_range_max && (
                  <small style={{ display:'block', color:'#666', marginTop:4 }}>{fmt(form.price_range_max)}</small>
                )}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#003566', marginBottom:6 }}>
                Expected Price (₹) *
              </label>
              <input
                className="form-input price-input"
                type="number"
                name="expected_price"
                placeholder="e.g. 500000"
                value={form.expected_price}
                onChange={handleChange}
                style={{ fontSize:18, fontWeight:700 }}
                min="1"
              />
              {form.expected_price && (
                <small style={{ display:'block', color:'#666', marginTop:4 }}>{fmt(form.expected_price)}</small>
              )}
            </div>

            {/* Preview Card */}
            {(form.price_range_min || form.price_range_max || form.expected_price) && (
              <div style={{
                background:'#f5f5f5', borderRadius:10, padding:'14px 18px',
                border:'2px solid #e0e0e0', marginBottom:16,
              }}>
                <p style={{ fontSize:12, fontWeight:700, color:'#666', textTransform:'uppercase',
                  letterSpacing:0.5, margin:'0 0 10px' }}>Price Summary Preview</p>
                <div style={{ display:'flex', gap:24 }}>
                  <div>
                    <p style={{ fontSize:11, color:'#999', margin:'0 0 2px' }}>Range</p>
                    <p style={{ fontSize:14, fontWeight:700, color:'#003566', margin:0 }}>
                      {fmt(form.price_range_min)} – {fmt(form.price_range_max)}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize:11, color:'#999', margin:'0 0 2px' }}>Expected</p>
                    <p style={{ fontSize:14, fontWeight:700, color:'#856404', margin:0 }}>
                      {fmt(form.expected_price)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div style={{
                background:'#dc354520', border:'1px solid #dc3545', borderRadius:8,
                padding:'12px 16px', color:'#dc3545', fontSize:14, fontWeight:600,
              }}>
                ⚠ {error}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-modal-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn-modal-submit"
            onClick={handleSubmit}
            disabled={loading}
            style={{
              background: loading ? '#ccc' : 'linear-gradient(135deg,#FFD60A,#FFC107)',
              color:'#003566', fontWeight:700,
            }}
          >
            {loading ? 'Notifying...' : '📢 Notify Dealers'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotifyDealersModal;